import { useState, useCallback } from 'react';
import axios from 'axios';
import { useLiveRefresh } from './useLiveRefresh.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

/**
 * Health alerts hook.
 *
 * Loads open health alerts from HavunCore Laravel -- through the Node backend,
 * which checks the login: Laravel answers the server only -- and keeps them live through
 * useLiveRefresh: a Socket.io signal, a restored connection or a return to the foreground
 * refetches the authoritative list (no polling).
 */
export function useHealthAlerts(token) {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    const refetch = useCallback(async () => {
        if (!token) return;
        try {
            const res = await axios.get(`${API_URL}/api/meldingen/alerts`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAlerts(res.data?.data || []);
        } catch (err) {
            console.error('Failed to load health alerts:', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const dismiss = useCallback(async (id) => {
        // Optimistic removal, then confirm with the server.
        setAlerts((prev) => prev.filter((a) => a.id !== id));
        try {
            await axios.post(`${API_URL}/api/meldingen/alerts/${id}/dismiss`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (err) {
            console.error('Failed to dismiss alert:', err);
            refetch();
        }
    }, [refetch, token]);

    useLiveRefresh(token, refetch);

    const openCount = alerts.filter((a) => a.status === 'open').length;

    return { alerts, openCount, loading, refetch, dismiss };
}

export default useHealthAlerts;
